
import React, { useContext, useMemo } from 'react';
import { AppContext } from '../App.tsx';
import { AppStep } from '../types.ts';
import { FileCode, Maximize2, Share2, ArrowLeft } from 'lucide-react';

const PreviewPage: React.FC = () => {
  const ctx = useContext(AppContext);

  const iframeContent = useMemo(() => {
    if (!ctx?.state.generatedCode) return '';
    const blob = new Blob([ctx.state.generatedCode], { type: 'text/html' });
    return URL.createObjectURL(blob);
  }, [ctx?.state.generatedCode]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => ctx?.dispatch({ type: 'SET_STEP', payload: AppStep.CODE_VIEW })}
            className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-2xl font-bold">Live Preview</h2>
            <p className="text-slate-400 text-sm">Interactive environment rendered with Tailwind.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-sm font-semibold transition-all">
            <Share2 size={16} />
            Share
          </button>
          <button 
            onClick={() => ctx?.dispatch({ type: 'SET_STEP', payload: AppStep.CODE_VIEW })}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-sm font-bold transition-all border border-slate-600"
          >
            <FileCode size={16} />
            View Code
          </button>
        </div>
      </div>

      <div className="relative group bg-white rounded-3xl border border-slate-800 shadow-2xl h-[700px] overflow-hidden">
        {/* Device Control Bar Mockup */}
        <div className="h-10 bg-slate-100 border-b border-slate-200 flex items-center justify-center gap-2">
          <div className="w-24 h-2 bg-slate-200 rounded-full" />
        </div>
        <iframe 
          src={iframeContent}
          title="Generated UI Preview"
          className="w-full h-full border-none bg-white"
        />
        <div className="absolute bottom-6 right-6 p-4 bg-slate-900/80 backdrop-blur-md rounded-2xl border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-4 text-xs font-bold text-slate-400">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              100% RESPONSIVE
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-indigo-500" />
              TAILWIND V3
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800">
        <h4 className="text-xl font-bold mb-4">Pipeline Metrics</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800">
            <p className="text-xs text-slate-500 font-bold uppercase mb-1">Processing Time</p>
            <p className="text-2xl font-bold text-indigo-400">12.4s</p>
          </div>
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800">
            <p className="text-xs text-slate-500 font-bold uppercase mb-1">Layout Accuracy</p>
            <p className="text-2xl font-bold text-emerald-400">99.2%</p>
          </div>
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800">
            <p className="text-xs text-slate-500 font-bold uppercase mb-1">Tailwind Optimized</p>
            <p className="text-2xl font-bold text-fuchsia-400">True</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPage;
